/** CSV Merge Portal — import source kind and localStorage keys. */

export const CSV_MERGE_SOURCE_KIND = 'ready-advice-csv-merge';

/** Manually-entered AR clients (Add AR Client) — same unreleased bucket mechanics as CSV merge. */
export const MANUAL_AR_SOURCE_KIND = 'manual-ar-entry';

export const CSV_MERGE_DEMO_MODE_KEY = 'csv-merge-demo-mode';

export const CSV_MERGE_FILE_NOTE_CATEGORY = 'CSV Merge';

export function defaultCsvMergeState() {
  return {
    releasedAt: null,
    releasedBy: null,
    mergeBatchId: null,
    sourceRows: [],
    editedFields: {},
    parked: null,
    reviewedAt: null,
    reviewedBy: null,
  };
}

/**
 * Why a client was set aside instead of released. Parking is reversible, so these are
 * routing labels rather than verdicts — a parked client is worked again once the reason
 * is resolved.
 */
export const CSV_MERGE_PARK_REASONS = [
  { value: 'data-missing-chase-crm', label: 'Data missing — chase the CRM' },
  { value: 'awaiting-risk-profile', label: 'Awaiting risk profile / goals' },
  { value: 'insurance-mismatch', label: 'Insurance does not match' },
  { value: 'duplicate-record', label: 'Duplicate record' },
  { value: 'client-departed', label: 'Client has left' },
  { value: 'wrong-team-or-adviser', label: 'Wrong team or adviser' },
  { value: 'other', label: 'Other (explain)' },
];

export function parkReasonLabel(value) {
  return CSV_MERGE_PARK_REASONS.find((r) => r.value === value)?.label || value || 'Parked';
}

export function isParkedCsvMerge(client) {
  return !!client?.csvMerge?.parked?.at;
}

/**
 * Reviewed clients leave the Pending queue. With several auditors working simultaneously and
 * everyone starting at the top, this is what stops them landing on the same client.
 */
export function isReviewedCsvMerge(client) {
  return !!client?.csvMerge?.reviewedAt;
}

/**
 * `csvMerge.editedFields` keys must be FLAT. Written as a Firestore field path,
 * `csvMerge.editedFields.personal.contactNumber` would create nested maps
 * (editedFields > personal > contactNumber) rather than one key, so dots are encoded.
 *
 * Array cells are keyed by ROW ID, not index — an index goes stale the moment a row above
 * it is deleted, which would mis-attribute the provenance of every row after it.
 */
export function encodeEditedFieldKey(path, rowId, itemPath) {
  const base = rowId ? `${path}#${rowId}.${itemPath}` : path;
  return base.replace(/\./g, '__');
}

export function decodeEditedFieldKey(key) {
  return String(key || '').replace(/__/g, '.');
}

/** @returns {{ at?, by?, from? }|null} provenance for one field, if it was hand-edited. */
export function getEditedFieldMeta(client, path, rowId, itemPath) {
  const map = client?.csvMerge?.editedFields;
  if (!map) return null;
  const entry = map[encodeEditedFieldKey(path, rowId, itemPath)];
  if (!entry) return null;
  return entry === true ? {} : entry;
}

export function normaliseCsvMergeState(clientData) {
  return { ...defaultCsvMergeState(), ...(clientData?.csvMerge || {}) };
}

export function isUnreleasedCsvMerge(client) {
  if (!client) return false;
  return (
    client.importSource?.kind === CSV_MERGE_SOURCE_KIND
    && !client.csvMerge?.releasedAt
  );
}

export function isUnreleasedManualAr(client) {
  if (!client) return false;
  return (
    client.importSource?.kind === MANUAL_AR_SOURCE_KIND
    && !client.csvMerge?.releasedAt
  );
}

/** Any unreleased bucketed client — CSV merge import or manual AR entry. */
export function isUnreleasedClient(client) {
  return isUnreleasedCsvMerge(client) || isUnreleasedManualAr(client);
}

/** Display name for a csv-merge client — falls back to email, then doc id. */
export function getClientLabel(client) {
  const p = client?.personal;
  if (p?.firstName && p?.surname) return `${p.firstName} ${p.surname}`;
  return p?.email || client?.id || 'Unnamed';
}
